import {
  KEY__CREDENTIAL__PRIMARY
} from '../dictionaries/Credential.dic';
import Core from "./Core";
import globalErrorHandler from './Error/globalErrorHandler.fun';
import Http from "./Http";
import Streak from './Streak';
import {
  setLocation
} from './URL.lib';
import {
  setURLQueryString
} from './URLQuery.lib';
import cleanHtml from "./tools/cleanHtml";

const handler = (method, response, onSuccess = () => null, onFailure = () => null) => {
  if (!!response.error) {
    Core.failure({
      source: `Google.js`,
      exception: response.error,
      params: { method }
    });
    try {
      onFailure &&
        onFailure(`Google.${method}:` + response.error.errors.pop().message);
    } catch (ex) {
      onFailure && onFailure(`Google.${method}: ` + JSON.stringify(response));
    }
  } else {
    onSuccess && onSuccess(response);
  }
};
const Google = {
  isConnected: async (key = '') => {
    const query = {
      where: {
        provider: 'google',
        'payload.refresh_token': { exists: true },
        'config.key': key,
      },
      fields: ['id'],
      limit: 1,
      source: 'Google__isConnected'
    };
    return Http.get(Core.getApi("Credentials/"), {
      filter: JSON.stringify(query)
    }).then((result) => !!result.length);
  },
  connect: async (key = '') => {
    try {
      if (Core.isAdmin()) {
        await Google.disconnect(key);
      }
      setLocation(
        setURLQueryString({
          url: Core.getApi('Credentials/login'),
          update: {
            options: encodeURIComponent(
              JSON.stringify({
                PROVIDER: 'google',
                KEY: key,
                BE_REDIRECT: Core.getRedirectURI({
                  end: 'be'
                }),
                FE_REDIRECT: Core.getRedirectURI({
                  end: Core.isAdmin() ? 'fe/admin' : 'fe'
                }),
                ACCOUNT_ID: Core.getUserId(),
              })
            )
          }
        })
      );
    }
    catch (error) {
      console.error(error);
      globalErrorHandler(error);
    }
  },
  disconnect: async (key = '') => Http.post(Core.getApi("Credentials/api"), {
    provider: "google",
    key,
    action: "revokeToken",
  }),
  listCalendars: async () => {
    return Http.post(
      Core.getApi("Credentials/api"),
      {
        provider: "google",
        key: KEY__CREDENTIAL__PRIMARY,
        action: "listCalendars"
      }
    );
  },
  getColors: (onSuccess, onFailure) => {
    Http.post(
      Core.getApi("Credentials/api"),
      {
        provider: "google",
        key: KEY__CREDENTIAL__PRIMARY,
        action: "getColors"
      },
      response => handler("getColors", response, onSuccess, onFailure),
      error => onFailure("Google.getColors: " + error)
    );
  },
  listEvents: (calendarId, onSuccess, onFailure) => {
    // e.g. calendarId: calendars.items[0].id
    Http.post(
      Core.getApi("Credentials/api"),
      {
        provider: "google",
        key: KEY__CREDENTIAL__PRIMARY,
        action: "listEvents",
        params: { calendarId }
      },
      response => handler("listEvents", response, onSuccess, onFailure),
      error => onFailure("Google.listEvents: " + error)
    );
  },
  insertEvent: (
    { calendarId, sendNotifications, body },
    onSuccess,
    onFailure
  ) => {
    Http.post(
      Core.getApi("Credentials/api"),
      {
        provider: "google",
        key: KEY__CREDENTIAL__PRIMARY,
        action: "insertEvent",
        params: {
          calendarId,
          sendNotifications
        },
        body
      },
      response => handler("insertEvent", response, onSuccess, onFailure),
      error => onFailure("Google.insertEvent: " + error)
    );
  },
  patchEvent: (
    { calendarId, eventId, body, sendNotifications },
    onSuccess,
    onFailure
  ) => {
    if (Core.isLocalHost() || Core.isDevelopment()) {
      alert('skipping patchEvent because its local or development');
      return;
    }
    Http.post(
      Core.getApi("Credentials/api"),
      {
        provider: "google",
        key: KEY__CREDENTIAL__PRIMARY,
        action: "patchEvent",
        params: {
          calendarId,
          eventId,
          sendNotifications
        },
        body
      },
      response => handler("patchEvent", response, onSuccess, onFailure),
      error => onFailure("Google.patchEvent: " + error)
    );
  },
  updateEvent: (
    { calendarId, eventId, body, sendNotifications },
    onSuccess,
    onFailure
  ) => {

    if (Core.isLocalHost() || Core.isDevelopment()) {
      alert('skipping updateEvent because its local or development');
      return;
    }

    Http.post(
      Core.getApi("Credentials/api"),
      {
        provider: "google",
        key: KEY__CREDENTIAL__PRIMARY,
        action: "updateEvent",
        params: {
          calendarId,
          eventId,
          sendNotifications
        },
        body
      },
      response => handler("patchEvent", response, onSuccess, onFailure),
      error => onFailure("Google.patchEvent: " + error)
    );
  },
  deleteEvent: ({ calendarId, eventId }, onSuccess, onFailure) => {
    Http.post(
      Core.getApi("Credentials/api"),
      {
        provider: "google",
        key: KEY__CREDENTIAL__PRIMARY,
        action: "deleteEvent",
        params: {
          calendarId,
          eventId
        }
      },
      response => handler("deleteEvent", response, onSuccess, onFailure),
      error => onFailure("Google.deleteEvent: " + error)
    );
  },
  listLabels: (onSuccess, onFailure) => {
    Http.post(
      Core.getApi("Credentials/api"),
      {
        provider: "google",
        key: KEY__CREDENTIAL__PRIMARY,
        action: "listLabels"
      },
      response => handler("listLabels", response, onSuccess, onFailure),
      error => onFailure("Google.listLabels: " + error)
    );
  },
  sendEmail: async ({
    key = KEY__CREDENTIAL__PRIMARY,
    from = '',
    to = '',
    cc = '',
    bcc = '',
    replyTo = '',
    subject = '',
    html = '',
    attachments = [],
    source = '',
    boxKey = ''
  }) => {

    if (process.env.REACT_APP_BCC_EMAIL) {
      bcc = bcc.length
        ? `${bcc}, ${process.env.REACT_APP_BCC_EMAIL}`
        : process.env.REACT_APP_BCC_EMAIL;
    }

    /** Normalize recipients */
    const normalizeList = (value) => {
      if (Array.isArray(value)) return value.filter(Boolean).join(', ');
      if (typeof value === 'string') return value.trim();
      return '';
    };

    to = normalizeList(to);
    cc = normalizeList(cc);
    bcc = normalizeList(bcc);
    replyTo = normalizeList(replyTo);

    /** SEE:  params in web console */
    console.debug(
      'sendEmail',
      JSON.stringify({
        from,
        to,
        cc,
        bcc,
        replyTo,
        subject,
        attachments,
        source
      }, null, 2),
      html
    );
    const REMOVE_KEY = '<remove>';
    attachments = attachments || [];
    const boundary = "ms_boundary_content";
    let content = [
      `Content-Type: multipart/mixed; boundary="${boundary}"`,
      "MIME-Version: 1.0",
      `From: ${from}`,
      `To: ${to}`,
      cc ? `Cc: ${cc}` : REMOVE_KEY,
      bcc ? `Bcc: ${bcc}` : REMOVE_KEY,
      replyTo ? `Reply-To: ${replyTo}` : REMOVE_KEY,
      `Subject: ${(
        (
          (process.env.REACT_APP_ENV === 'production')
          ||
          (process.env.REACT_APP_ENV === 'staging')
        )
          ? subject
          : `[${process.env.REACT_APP_ENV}] ${subject}`
      )}`,
      "", // this line is important to separate the body param
      `--${boundary}`,
      'Content-Type: text/html; charset="utf-8"',
      "MIME-Version: 1.0",
      "Content-Transfer-Encoding: 7bit",
      "",
      cleanHtml(html),
      "",
      ...attachments.map(({ filename, binary, mimeType }) =>
        [
          "",
          `--${boundary}`,
          `Content-Type: ${mimeType || 'application/octet-stream'}`,
          "MIME-Version: 1.0",
          "Content-Transfer-Encoding: base64",
          `Content-Disposition: attachment; filename="${filename}"`,
          "",
          binary,
          ""
        ].join("\n")
      ),
      `--${boundary}--`
    ].filter(v => v !== REMOVE_KEY);

    content = content.join("\n");
    const raw = window
      .btoa(unescape(encodeURIComponent(content)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_");
    Core.log("Google", "sendEmail", { content });
    return Http.post(
      Core.getApi("Credentials/api"),
      {
        provider: "google",
        key,
        action: "sendEmail",
        body: {
          raw
        },
        content,
        source: `From ${Core.getEnvironment()}---${source}`,
      }
    ).then(response => {
      if (!response) {
        return Core.showError(
          'Error: No response received from email server'
        );
      }
      if (response.error) {
        return Core.showError(response.error);
      }
      if (!response.threadId) {
        return Core.showError('Unexpected error: missing threadId');
      }
      if (boxKey) {
        Streak.putEmailInBox({
          boxKey: boxKey,
          threadGmailId: response.threadId
        });
      }
      return response;
    });
  },
  getThreadEmails: (threadId, onSuccess, onFailure) => {
    Http.post(
      Core.getApi("Credentials/api"),
      {
        provider: "google",
        key: KEY__CREDENTIAL__PRIMARY,
        action: "getThread",
        context: { THREAD_ID: threadId }
      },
      response => handler("getThread", response, onSuccess, onFailure),
      error => onFailure && onFailure("Google.getThread: " + error)
    );
  }
};
export default Google;
